import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@fragments';
import './styles.scss';

const VAR_RE = /\{\{([A-Za-z0-9_.]+)\}\}/g;

// Quote-parity scan up to index — good enough for JSON-ish content to decide
// whether a position sits inside a "string".
function isInsideString(text, index) {
    let inString = false;
    for (let i = 0; i < index; i++) {
        const ch = text[i];
        if (inString && ch === '\\') i++;
        else if (ch === '"') inString = !inString;
    }
    return inString;
}

// The textarea owns ALL visible text (so the caret can never desync from the
// glyphs); this underlay only paints pill backgrounds behind {{variables}} —
// its own text is transparent and exists purely to position the pills.
function renderUnderlay(text, knownSet) {
    const nodes = [];
    let last = 0;
    let match;
    let i = 0;
    VAR_RE.lastIndex = 0;
    while ((match = VAR_RE.exec(text)) !== null) {
        if (match.index > last) {
            nodes.push(<span key={`p${i++}`}>{text.slice(last, match.index)}</span>);
        }
        const known = knownSet.has(match[1]);
        nodes.push(
            <span
                className={`json-template-editor__var${known ? '' : ' json-template-editor__var--unknown'}`}
                key={`v${i++}`}
            >
                {match[0]}
            </span>
        );
        last = match.index + match[0].length;
    }
    if (last < text.length) {
        nodes.push(<span key="t">{text.slice(last)}</span>);
    }
    return nodes;
}

export default function JsonTemplateEditor({ label, value, onChange, variables, placeholder, helper }) {
    const taRef = useRef(null);
    const boxRef = useRef(null);
    const pendingCaret = useRef(null);

    // {query, start} — start = index of the opening "{{"
    const [suggest, setSuggest] = useState(null);
    const [activeIdx, setActiveIdx] = useState(0);

    const text = value ?? '';

    // Programmatic caret moves must land AFTER React commits the new value —
    // a rAF can fire before the commit and the caret then jumps to the end.
    useLayoutEffect(() => {
        if (pendingCaret.current === null || !taRef.current) return;
        const el = taRef.current;
        el.focus();
        el.setSelectionRange(pendingCaret.current, pendingCaret.current);
        pendingCaret.current = null;
    }, [text]);

    const knownSet = useMemo(() => new Set(variables.map(v => v.value)), [variables]);

    const options = useMemo(() => {
        if (!suggest) return [];
        const query = suggest.query.toLowerCase();
        return variables.filter(v => v.value.toLowerCase().includes(query));
    }, [suggest, variables]);

    const invalidJson = useMemo(() => {
        if (!text.trim()) return false;
        try {
            const parsed = JSON.parse(text);
            return parsed === null || typeof parsed !== 'object';
        } catch {
            return true;
        }
    }, [text]);

    function setCaret(position) {
        pendingCaret.current = position;
    }

    // Any internal textarea scroll would desync the pill underlay (the box is
    // the only intended scroller) — transfer it and re-zero.
    function handleTextareaScroll(event) {
        const el = event.target;
        if (el.scrollTop !== 0) {
            if (boxRef.current) boxRef.current.scrollTop += el.scrollTop;
            el.scrollTop = 0;
        }
        if (el.scrollLeft !== 0) el.scrollLeft = 0;
    }

    function refreshSuggest(el) {
        const pos = el.selectionStart;
        if (pos !== el.selectionEnd) { setSuggest(null); return; }
        const before = el.value.slice(0, pos);
        const match = before.match(/\{\{([A-Za-z0-9_.]*)$/);
        if (!match) { setSuggest(null); return; }

        setSuggest({ query: match[1], start: pos - match[1].length - 2 });
        setActiveIdx(0);
    }

    // The textarea never scrolls itself (it spans the full content height), so
    // the browser won't pull the caret into view — nudge the box manually.
    function ensureCaretVisible(el) {
        const box = boxRef.current;
        if (!box || box.scrollHeight <= box.clientHeight) return;
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
        const line = el.value.slice(0, el.selectionStart).split('\n').length;
        const caretBottom = line * lineHeight + lineHeight;
        if (caretBottom > box.scrollTop + box.clientHeight) {
            box.scrollTop = caretBottom - box.clientHeight;
        } else if (caretBottom - lineHeight * 2 < box.scrollTop) {
            box.scrollTop = Math.max(0, caretBottom - lineHeight * 2);
        }
    }

    function handleInput(event) {
        const el = event.target;
        let next = el.value;

        // Typing the closing "}" of a known {{variable}} outside a string
        // auto-quotes it, keeping the template valid JSON.
        if (event.nativeEvent?.data === '}') {
            const pos = el.selectionStart;
            const completed = next.slice(0, pos).match(/\{\{([A-Za-z0-9_.]+)\}\}$/);
            if (completed && knownSet.has(completed[1])) {
                const start = pos - completed[0].length;
                if (!isInsideString(next, start)) {
                    next = `${next.slice(0, start)}"${completed[0]}"${next.slice(pos)}`;
                    setCaret(pos + 2);
                }
            }
        }

        onChange(next);
        refreshSuggest(el);
        ensureCaretVisible(el);
    }

    function pick(variable) {
        const el = taRef.current;
        if (!el || !suggest) return;
        const pos = el.selectionStart;
        let after = text.slice(pos);
        // Consume closing braces the user may have already typed
        if (after.startsWith('}}')) after = after.slice(2);
        else if (after.startsWith('}')) after = after.slice(1);

        // JSON needs the placeholder inside a string — quote it unless the
        // insertion point already sits within one.
        const token = isInsideString(text, suggest.start)
            ? `{{${variable}}}`
            : `"{{${variable}}}"`;

        const next = `${text.slice(0, suggest.start)}${token}${after}`;
        onChange(next);
        setSuggest(null);
        setCaret(suggest.start + token.length);
    }

    function formatJson() {
        const parsed = JSON.parse(text);
        const pretty = JSON.stringify(parsed, null, 2);
        if (pretty === text) return;
        onChange(pretty);
        setCaret(pretty.length);
    }

    function insertIndent(el) {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        onChange(`${text.slice(0, start)}  ${text.slice(end)}`);
        setCaret(start + 2);
    }

    function handleKeyDown(event) {
        const suggesting = suggest !== null && options.length > 0;

        if (suggesting) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIdx(i => (i + 1) % options.length);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIdx(i => (i - 1 + options.length) % options.length);
                return;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                pick(options[activeIdx].value);
                return;
            }
            if (event.key === 'Escape') {
                setSuggest(null);
                return;
            }
        }

        // Tab pretty-prints the template instead of moving focus; while the
        // JSON is invalid it indents, so the key always visibly does something.
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            event.stopPropagation();
            if (!text.trim() || invalidJson) {
                insertIndent(event.target);
            } else {
                formatJson();
            }
        }
    }

    const showSuggest = suggest !== null && options.length > 0;

    return (
        <div className="json-template-editor">
            {label && <Text.label contrast={8}>{label}</Text.label>}

            <div className="json-template-editor__anchor">
                <div ref={boxRef} className={`json-template-editor__box${invalidJson ? ' json-template-editor__box--error' : ''}`}>
                    <div className="json-template-editor__content">
                        <div className="json-template-editor__underlay" aria-hidden="true">
                            {renderUnderlay(text, knownSet)}
                            {'\n'}
                        </div>
                        <textarea
                            ref={taRef}
                            className="json-template-editor__input"
                            value={text}
                            placeholder={placeholder}
                            spellCheck={false}
                            onChange={handleInput}
                            onSelect={event => refreshSuggest(event.target)}
                            onKeyDown={handleKeyDown}
                            onScroll={handleTextareaScroll}
                            onBlur={() => setSuggest(null)}
                        />
                    </div>
                </div>

                {showSuggest && (
                    <div className="json-template-editor__suggest">
                        {options.map((option, idx) => (
                            <div
                                key={option.value}
                                className={`json-template-editor__suggest-item${idx === activeIdx ? ' json-template-editor__suggest-item--active' : ''}`}
                                onMouseDown={event => { event.preventDefault(); pick(option.value); }}
                                onMouseEnter={() => setActiveIdx(idx)}
                            >
                                <span className="json-template-editor__suggest-name">{`{{${option.value}}}`}</span>
                                <Text.caption>{option.label}</Text.caption>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {invalidJson
                ? <Text.caption className="json-template-editor__error-hint">JSON inválido — o template precisa ser um objeto ou lista.</Text.caption>
                : (helper && <Text.caption>{helper}</Text.caption>)}
        </div>
    );
}
